import { css } from "@emotion/react"
import get from "lodash/get"
import React, { useCallback, useState } from "react"
import { Link } from "react-router-dom"
import { Event } from "../../../../models"
import Routes from "../../../../startup/routes"
import { colors } from "../../../../styles/variables"
import { eventTimeZone, formatDateAndTime, formatTime, getTimeFromNow } from "../../../../util/dateTime"
import Analytics from "../../misc/Analytics"
import Loading from "../../misc/Loading"
import ResponsiveImage from "../../misc/MediaPresets"
import Modal from "../../misc/Modal"
import TimeZoneLabel from "../../misc/TimeZoneLabel"
import PublishStateAction from "../publishState/PublishStateAction"
import YourRecordInlineDelete from "../your/YourRecordInlineDelete"
import DuplicateInquiryModal from "./DuplicateInquiryModal"
import SubmittedAsBadge from "./SubmittedAsBadge"

import "chartkick/chart.js"
import { LineChart } from "./react-chartkick"
import { IconGlyph } from '../../../../icons/IconGlyph'

const Address: React.FC<{ event: Event }> = ({ event }) => {
  const { place } = event
  const addressName = place ? get(place, "name") : get(event, "tempPlace.name")
  const streetAddress = place ? get(place, "address.streetAddress") : get(event, "tempPlace.address.streetAddress")

  return (
    <div>
      {addressName}
      <br />
      {streetAddress}
    </div>
  )
}

type Props = { record: Event; removeRecord: (eventId?: string) => void }
const YourEventsItem: React.FC<Props> = ({ record: event, removeRecord }) => {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false)
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const openDeleteModal = useCallback((): void => setIsDeleteModalOpen(true), [])
  const closeDeleteModal = useCallback(() => setIsDeleteModalOpen(false), [])
  const openDuplicateModal = useCallback((): void => setIsDuplicateModalOpen(true), [])
  const closeDuplicateModal = useCallback(() => setIsDuplicateModalOpen(false), [])

  const removeEvent = useCallback((): void => {
    closeDeleteModal()
    setIsLoading(true)
    removeRecord(event.id)
  }, [closeDeleteModal, event, removeRecord])

  const isMerged = event.isMerged

  return (
    <article
      className="dash-slab"
      css={
        isMerged &&
        css`
          opacity: 0.7;
          background: #fafafa;
        `
      }
    >
      {/* Delete confirmation modal */}
      <Modal isOpen={isDeleteModalOpen} onRequestClose={closeDeleteModal}>
        <h1>Remove: {get(event, "title")}</h1>
        <hr style={{ margin: "1em 0" }} />
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
          <div>Are you sure you want to remove this event?</div>
          <div style={{ paddingTop: "20px" }}>
            <button type="button" className="btn btn-grey" onClick={closeDeleteModal}>
              Cancel
            </button>
            <button type="button" className="btn btn-red" onClick={removeEvent}>
              Remove
            </button>
          </div>
        </div>
      </Modal>

      {/* Duplicate inquiry modal */}
      <DuplicateInquiryModal event={event} isOpen={isDuplicateModalOpen} onClose={closeDuplicateModal} />

      <div className="dash-slab-photo">
        {isMerged ? (
          <ResponsiveImage publicId={get(event, "image.publicId")} width="140" height="90" />
        ) : (
          <Link to={Routes.event(event)}>
            <ResponsiveImage publicId={get(event, "image.publicId")} width="140" height="90" />
          </Link>
        )}
      </div>

      <div className="dash-slab-info">
        <h2
          className="dash-slab-heading"
          css={isMerged && css`color: #999;`}
        >
          <span css={isMerged && css`text-decoration: line-through;`}>{event.title}</span>
        </h2>

        <div className="dash-slab-detail">
          {event.allDay
            ? `${formatDateAndTime(event.startAt, eventTimeZone(event)).split(" @ ")[0]} - All Day`
            : `${formatDateAndTime(event.startAt, eventTimeZone(event))}${
                event.endAt ? " - " + formatTime(event.endAt, eventTimeZone(event)) : ""
              }`}
          {!event.allDay && <TimeZoneLabel timeZone={eventTimeZone(event)} on={event.startAt} />}
          <br />
          {getTimeFromNow(event.startAt)}
        </div>
        <div className="dash-slab-detail dash-slab-address">
          <Address event={event} />
        </div>
        <div css={css`margin-top: 4px;`}>
          <SubmittedAsBadge event={event} />
        </div>
      </div>

      {/* Middle column: graph for normal events, duplicate notice for merged */}
      {isMerged ? (
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 4px;
            font-size: 11px;
            color: #666;
            flex: 1;
            max-width: 400px;
          `}
        >
          <div css={css`display: flex; align-items: center; gap: 6px; flex-wrap: wrap;`}>
            <span
              css={css`
                display: inline-block;
                padding: 2px 6px;
                background: ${colors.clYellow};
                color: #5d4037;
                font-size: 10px;
                font-weight: 600;
                border-radius: 3px;
                text-transform: uppercase;
                white-space: nowrap;
              `}
            >
              Duplicate
            </span>
            <span>This event appears to be a duplicate of an event that already exists in our system.</span>
            {event.mergedIntoEvent && (
              <Link
                to={Routes.event(event.mergedIntoEvent)}
                css={css`
                  color: #1976d2 !important;
                  font-size: 11px !important;
                  text-decoration: underline !important;
                  white-space: nowrap;
                  &:hover {
                    color: #1565c0 !important;
                  }
                `}
              >
                See the original here.
              </Link>
            )}
          </div>
          <div>
            <button
              type="button"
              onClick={openDuplicateModal}
              css={css`
                background: none !important;
                border: none !important;
                padding: 0 !important;
                color: #1976d2 !important;
                font-size: 11px !important;
                cursor: pointer !important;
                text-decoration: underline !important;
                white-space: nowrap;
                &:hover {
                  color: #1565c0 !important;
                }
              `}
            >
              Did we make a mistake?
            </button>
          </div>
        </div>
      ) : (
        <>
          {event.analytics && (
            <LineChart
              legend={false}
              library={{ scales: { x: { display: false }, y: { display: false } }, elements: { point: { pointStyle: false } } }}
              data={event.analytics}
              width={200}
              height={90}
            />
          )}
          {event.numberAnalytics && <Analytics {...event.numberAnalytics} />}
        </>
      )}

      {!isMerged && (
        <div className="dash-slab-actions">
          <Link to={Routes.editEvent(event)} className="dash-slab-action">
            <PublishStateAction publishState={event.publishState} />
          </Link>
          <Link
            to={Routes.duplicateEvent(event)}
            className="dash-slab-action"
            title="Create a new event prefilled with this event's details"
          >
            <IconGlyph name="add-circle" color="blue" />
            <span className="dash-slab-action-text">Duplicate</span>
          </Link>
          {isLoading ? (
            <div className="dash-slab-action">
              <Loading className="isCentered isCollapsed isDark" />
            </div>
          ) : (
            <YourRecordInlineDelete onClick={openDeleteModal} />
          )}
        </div>
      )}
    </article>
  )
}
export default YourEventsItem
